import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, StatusBadge, fmtDate } from "@/components/ui-kit";
import { Calendar } from "@/components/ui/calendar";

export default function CalendarPage() {
  const [selected, setSelected] = useState(new Date());

  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: interpreters = [] } = useQuery({ queryKey: ["interpreters"], queryFn: async () => (await api.get("/interpreters")).data });

  const byDate = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      if (!p.scheduled_at) return;
      const d = new Date(p.scheduled_at).toDateString();
      (map[d] ||= []).push(p);
    });
    return map;
  }, [projects]);

  const dayList = byDate[selected?.toDateString?.()] || [];
  const clientName = (id) => clients.find(c => c.client_id === id)?.name || "—";
  const intpName = (id) => interpreters.find(i => i.interpreter_id === id)?.full_name || "—";

  const modifiers = useMemo(
    () => ({ scheduled: (d) => !!byDate[d.toDateString()] }),
    [byDate],
  );
  const modifiersClassNames = { scheduled: "bg-blue-100 text-blue-900 font-semibold rounded-md" };

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow="Schedule"
        title="Calendar"
        description="See all scheduled assignments, USCIS appointments, and interpreter workload."
      />
      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        <div className="card-flat p-4">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={setSelected}
            data-testid="calendar-picker"
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            className="rounded-md"
          />
          <div className="mt-4 text-xs text-slate-500">
            <span className="inline-block w-3 h-3 rounded bg-blue-100 mr-1.5 align-middle" /> Day with scheduled assignments
          </div>
        </div>

        <div className="card-flat">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display font-semibold text-slate-900">
              {selected?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{dayList.length} assignments</div>
          </div>
          <div className="p-5">
            {dayList.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No assignments on this date.</div>
            ) : (
              <ul className="space-y-3">
                {dayList.map((p) => (
                  <li key={p.project_id} className="border border-slate-200 rounded-md p-4 hover-lift bg-white" data-testid={`cal-item-${p.project_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display font-semibold text-slate-900">{p.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {clientName(p.client_id)} · {intpName(p.interpreter_id)} · {p.location || "—"}
                        </div>
                      </div>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="mt-2 text-xs font-mono text-slate-600">
                      {fmtDate(p.scheduled_at)} · {p.duration_hours}h · {p.language || "—"}
                      {p.uscis_case_number && <> · USCIS #{p.uscis_case_number}</>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
